import React, { useState, useRef, useMemo, useEffect } from 'react';

import moment from 'moment';

import DateRangePicker from 'components/DateRangePicker';
import SelectModal, { OptionsItem } from 'components/Selects/BookingSelectDate/components/Options';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';

interface Props {
  label?: string;
  initialValue?: string;
  initialDates?: { start: string; end: string };
  handleChangeDate: (start: string, end: string, period: string) => void;
  className?: string;
}

const PeriodSelect = ({ label, initialValue, initialDates, handleChangeDate, className }: Props) => {
  const { t } = useStoreTranslation();
  const periodRef = useRef(null);
  const [period, setPeriod] = useState<string>();
  const [show, setShow] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState<string>();
  const [filterEndDate, setFilterEndDate] = useState<string>();

  useEffect(() => {
    setPeriod(initialValue);
    setFilterStartDate(initialDates?.start);
    setFilterEndDate(initialDates?.end);
  }, [initialValue, initialDates]);

  const currentMonth = moment().format('MMMM');
  const getPreviousMonth = moment().subtract(1, 'months').endOf('month').format('MMMM');

  const periodItems: OptionsItem[] = [
    {
      text: t('periodSelect.options.lastDays'),
      value: 'thirtyDays',
    },
    {
      text: t('periodSelect.options.currentMonth', {
        month: currentMonth,
      }),
      value: 'current',
    },
    {
      text: t('periodSelect.options.previousMonth', {
        month: getPreviousMonth,
      }),
      value: 'previous',
    },
    { text: t('periodSelect.options.other'), value: 'date' },
  ];

  const makeValue = (key: string | undefined) => {
    if (period) {
      if (period === 'date') return formattedFilterDate;
      return periodItems.find((item) => item.value === key)?.text;
    }
    return '';
  };

  const handleChangePeriod = (value: string) => {
    let start: string | null = null;
    let end: string | null = null;

    if (value === 'date') {
      setPeriod('date');
      return setShow(true);
    }
    if (value === 'thirtyDays') {
      start = moment().subtract('30', 'days').toISOString();
      end = moment().toISOString();
    }
    if (value === 'previous') {
      const month = moment().subtract('1', 'month');
      start = month.startOf('month').toISOString();
      end = month.endOf('month').toISOString();
    }
    if (value === 'current') {
      start = moment().startOf('month').toISOString();
      end = moment().endOf('month').toISOString();
    }

    if (start && end) {
      setFilterStartDate(start);
      setFilterEndDate(end);
      handleChangeDate(start, end, value);
      setPeriod(value);
    }
  };

  const handleChangeDateSelect = (dates: { initialDate: string; finalDate: string }) => {
    setPeriod('date');
    setFilterStartDate(dates.initialDate);
    setFilterEndDate(dates.finalDate);
    handleChangeDate(dates.initialDate, dates.finalDate, period || '');
  };

  const formattedFilterDate = useMemo(() => {
    if (filterStartDate && filterEndDate) {
      return `${moment(filterStartDate).format('DD MMM')} - ${moment(filterEndDate).format('DD MMM')}`;
    }
  }, [filterStartDate, filterEndDate]);

  return (
    <div className={className}>
      <CustomSelect
        name="period"
        label={label ? label : t('periodSelect.label')}
        onChange={handleChangePeriod}
        placeHolder={t('periodSelect.label')}
        selectRef={periodRef}
        value={makeValue(period)}
      >
        {({ ...selectProps }: CustomSelectRenderProps) => (
          <SelectModal {...selectProps} selectRef={periodRef} items={periodItems} />
        )}
      </CustomSelect>
      <DateRangePicker
        allDate
        onlyDate={false}
        show={show}
        onChange={({ initialDate, finalDate }) =>
          handleChangeDateSelect({
            initialDate: initialDate.toISOString(),
            finalDate: (finalDate as Date).toISOString(),
          })
        }
        onClose={() => setShow(false)}
      />
    </div>
  );
};

export default PeriodSelect;
